import { FileText } from '@phosphor-icons/react';
import classNames from 'classnames';
import type { MouseEventHandler } from 'react';
import type { Price, PricingFormMode, Tier } from '../../types';
import CurrencyInput from '../../ui/CurrencyInput';
import DiscordIcon from '../../ui/icons/Discord';
import { currencyFormatter } from '../../utils/currencyFormatter';
import * as styles from '../Pricing.css';
import type { TPricingAttributes } from '../Pricing.types';
import ExternalCheckout from './ExternalCheckout';
import { FeatureItem } from './FeatureItem';
import { RecommendedBadge } from './RecommendedBadge';
import { Tab } from './Tab';
import { Tabs } from './Tabs';
import { renderExpiresInXDays } from './renderExpiresInXDays';

interface Props {
  attributes: TPricingAttributes;
  tier: Tier;
  isInternalCheckout: boolean;
  selectedPrice?: Price;
  donationAmountCents: number;
  setDonationAmountCents: (value: number) => void;
  isCtaDisabled: boolean;
  onClickPriceTab: (price: Price) => void;
  isSelected: boolean;
  currentFormMode?: PricingFormMode;
  hasDiscordAccess?: boolean;
}

const PriceBlock = ({
  attributes,
  tier,
  isInternalCheckout,
  selectedPrice,
  donationAmountCents,
  setDonationAmountCents,
  isCtaDisabled,
  onClickPriceTab,
  isSelected,
  currentFormMode,
  hasDiscordAccess,
}: Props) => {
  const shouldShowRecommendedBadge =
    attributes?.recommendedObjectType === 'price' && attributes?.recommendedPriceId === selectedPrice?.id;

  const features = selectedPrice?.features || [];
  const hasFeatures = features.length > 0;
  const shouldShowFeatureDivider = hasFeatures || hasDiscordAccess;

  const premiumOfferDetails = [
    selectedPrice?.premium_offer?.custom_terms || selectedPrice?.premium_offer?.terms,
    selectedPrice?.premium_offer?.description,
    selectedPrice?.premium_offer?.expires_at && renderExpiresInXDays(selectedPrice?.premium_offer?.expires_at),
  ].filter(Boolean);
  const hasPremiumOfferDetails = premiumOfferDetails.length > 0;

  const { original_amount_cents, amount_cents, interval } = selectedPrice || {};
  const isDiscounted = amount_cents !== original_amount_cents && original_amount_cents;
  const cents = isDiscounted ? amount_cents : original_amount_cents;
  const isDonation = interval === 'donation';
  const showDonationAmountInput = isDonation && isInternalCheckout;
  const showDiscountedPrice = isDiscounted && !isDonation;

  const handleClickPriceTab: (price: Price) => MouseEventHandler<HTMLButtonElement> = (price: Price) => (e) => {
    e.stopPropagation();
    onClickPriceTab(price);
  };

  const isFreePlan = selectedPrice?.id === 'free';
  const externalCheckoutCTAText = (isFreePlan ? tier.cta : selectedPrice?.cta) || undefined;

  return (
    <>
      {shouldShowRecommendedBadge && <RecommendedBadge text={attributes?.recommendedBadgeText || 'Recommended'} />}
      <div className="flex flex-col justify-between w-full h-full gap-4">
        <div className="flex flex-col gap-y-4">
          <div className="flex flex-col gap-y-4">
            <h1
              className={classNames(
                'text-xl font-semibold text-left m-0 p-0 w-full leading-none',
                isSelected ? styles.tierNameSelected : styles.tierName,
              )}
            >
              {tier.name}
            </h1>
            <div className={classNames('flex flex-row flex-wrap font-semibold items-center gap-1')}>
              {showDiscountedPrice && selectedPrice && (
                <p className={classNames('line-through leading-none', styles.tierOfferPrice)}>
                  {currencyFormatter({ currency: selectedPrice.currency }).format(
                    selectedPrice.original_amount_cents / 100,
                  )}
                </p>
              )}

              <div className="flex flex-row items-end w-full gap-1">
                {!showDonationAmountInput && (
                  <p className={classNames('leading-none', styles.tierPrice)}>
                    {currencyFormatter({ currency: selectedPrice?.currency }).format((cents || 0) / 100)}
                  </p>
                )}
                {showDonationAmountInput && (
                  <CurrencyInput
                    name={`donation-amount-${selectedPrice?.id}`}
                    value={donationAmountCents}
                    onChange={(value: number) => setDonationAmountCents(value)}
                    minCents={100}
                    maxCents={100000}
                    currency={selectedPrice?.currency}
                    className="w-full"
                    required
                  />
                )}
                {selectedPrice?.denominator && (
                  <p className={classNames('leading-none mb-1', styles.tierPriceDenominator)}>
                    /{selectedPrice.denominator}
                  </p>
                )}
              </div>
            </div>
          </div>

          {tier.prices.length > 1 ? (
            <Tabs>
              {tier.prices.map((price) => {
                const isSelected = price.id === selectedPrice?.id;
                const tabLabel = price.denominator || price.interval.split('_').join(' ');

                return (
                  <Tab key={price.id} isSelected={isSelected} label={tabLabel} onClick={handleClickPriceTab(price)} />
                );
              })}
            </Tabs>
          ) : null}

          <p
            className={classNames(
              'w-full leading-normal text-sm font-normal text-left',
              isSelected ? styles.tierDescriptionSelected : styles.tierDescription,
            )}
          >
            {tier.description}
          </p>

          {hasPremiumOfferDetails && (
            <ul className="flex flex-col gap-2">
              {premiumOfferDetails.map((detail, index) => (
                <li key={index} className="flex flex-row gap-3 items-start text-left">
                  <FileText size={20} className={classNames(styles.tierOfferDetailsIconColor)} />
                  <span className={classNames('text-sm leading-normal font-normal', styles.tierOfferDetailsTextColor)}>
                    {detail}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {(hasFeatures || hasDiscordAccess) && (
            <div className="flex flex-col gap-4">
              {shouldShowFeatureDivider && <hr className="border-t" />}
              <div className="flex flex-col gap-2">
                {hasFeatures && (
                  <ul className="flex flex-col gap-2">
                    {features.map((feature, index) => (
                      <li key={index} className="flex flex-row gap-3 items-start text-left">
                        <FeatureItem feature={feature} isSelected={isSelected} />
                      </li>
                    ))}
                  </ul>
                )}
                {hasDiscordAccess && (
                  <div className="flex flex-row gap-3 items-center text-left">
                    <DiscordIcon aria-hidden className="h-4 w-4 shrink-0" />
                    <span
                      className={classNames(
                        'text-sm leading-normal font-normal',
                        isSelected ? styles.tierFeatureTextColorSelected : styles.tierFeatureTextColor,
                      )}
                    >
                      Discord access included
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {!isInternalCheckout && currentFormMode === 'individual' && (
          <ExternalCheckout
            borderRadius={attributes?.buttonBorderRadius?.toString() ?? ''}
            ctaText={externalCheckoutCTAText}
            isFreePlan={isFreePlan}
            isCtaDisabled={isCtaDisabled}
          />
        )}
      </div>
    </>
  );
};

export default PriceBlock;
